import { recordErrorInSentry } from "../../utils/init-sentry.js";
import type { TableCallParticipantDirectory } from "./table-call-participant-directory.js";
import type {
  MeetingClient,
  TableCallAudioReadiness,
  TableCallStatus,
} from "./table-call.context.js";

export type TableCallAudioReportDirection =
  | "input"
  | "output"
  | "receive"
  | "publish"
  | "sdk"
  | "unknown";

export type TableCallAudioIssueKind =
  | "permission-denied"
  | "no-device"
  | "device-busy-or-aborted"
  | "playback-blocked"
  | "unknown";

export interface AudioSnapshot {
  status: TableCallStatus;
  audioReadiness: TableCallAudioReadiness;
  meetingIsCurrent: boolean;
  selfAudioEnabled: boolean | null;
  selfAudioTrackReadyState: MediaStreamTrackState | null;
  selfAudioTrackMuted: boolean | null;
  currentAudioInputId: string | null;
  currentAudioOutputId: string | null;
  preferredAudioInputId: string | null;
  preferredAudioOutputId: string | null;
  remoteAudioEnabledParticipantCount: number;
  remoteLiveAudioTrackCount: number;
  remoteMissingLiveAudioParticipantIds: string[];
}

interface BuildAudioSnapshotArgs {
  meeting: MeetingClient | null;
  currentMeeting: MeetingClient | null;
  status: TableCallStatus;
  audioReadiness: TableCallAudioReadiness;
  preferredMicrophoneId: string | null;
  preferredSpeakerId: string | null;
  participantDirectory: TableCallParticipantDirectory;
}

type ReportTableCallAudioErrorArgs = BuildAudioSnapshotArgs & {
  event: string;
  direction: TableCallAudioReportDirection;
  operation: string;
  issueKind?: TableCallAudioIssueKind;
  target?: {
    tableId: string | null;
    fluidId: string | null;
  } | null;
  extra?: Record<string, unknown>;
};

const getRemoteAudioDiagnostics = (
  participantDirectory: TableCallParticipantDirectory
) => {
  const remoteAudioParticipants = participantDirectory
    .getRemoteAudio()
    .filter(({ remote }) => remote.audioEnabled);

  return {
    remoteAudioEnabledParticipantCount: remoteAudioParticipants.length,
    remoteLiveAudioTrackCount: remoteAudioParticipants.filter(
      ({ track }) => track?.readyState === "live"
    ).length,
    remoteMissingLiveAudioParticipantIds: remoteAudioParticipants
      .filter(({ track }) => track?.readyState !== "live")
      .map(({ remote }) => remote.peerId),
  };
};

const getSelfAudioDiagnostics = (meeting: MeetingClient | null) => ({
  selfAudioEnabled: meeting?.self.audioEnabled ?? null,
  selfAudioTrackMuted:
    meeting?.self.audioTrack?.muted === undefined
      ? null
      : Boolean(meeting.self.audioTrack.muted),
  selfAudioTrackReadyState: meeting?.self.audioTrack?.readyState ?? null,
});

const PLAYBACK_BLOCKED_ERROR_PATTERN = /play\(\)|autoplay|user gesture/iu;

export const buildAudioSnapshot = ({
  meeting,
  currentMeeting,
  status,
  audioReadiness,
  preferredMicrophoneId,
  preferredSpeakerId,
  participantDirectory,
}: BuildAudioSnapshotArgs): AudioSnapshot => {
  try {
    const currentDevices = meeting?.self.getCurrentDevices();
    const remoteAudioDiagnostics =
      getRemoteAudioDiagnostics(participantDirectory);
    const selfAudioDiagnostics = getSelfAudioDiagnostics(meeting);

    return {
      audioReadiness,
      currentAudioInputId: currentDevices?.audio?.deviceId ?? null,
      currentAudioOutputId: currentDevices?.speaker?.deviceId ?? null,
      meetingIsCurrent: Boolean(meeting && meeting === currentMeeting),
      preferredAudioInputId: preferredMicrophoneId,
      preferredAudioOutputId: preferredSpeakerId,
      ...remoteAudioDiagnostics,
      ...selfAudioDiagnostics,
      status,
    };
  } catch {
    return {
      audioReadiness,
      currentAudioInputId: null,
      currentAudioOutputId: null,
      meetingIsCurrent: Boolean(meeting && meeting === currentMeeting),
      preferredAudioInputId: preferredMicrophoneId,
      preferredAudioOutputId: preferredSpeakerId,
      remoteAudioEnabledParticipantCount: 0,
      remoteLiveAudioTrackCount: 0,
      remoteMissingLiveAudioParticipantIds: [],
      ...getSelfAudioDiagnostics(null),
      status,
    };
  }
};

export const classifyAudioIssueKind = (
  error: unknown,
  rtkPermissionMessage: string | null = null
): TableCallAudioIssueKind => {
  const errorLike = error as { name?: string; message?: string } | undefined;
  const errorName = errorLike?.name ?? String(error);
  const errorMessage = errorLike?.message ?? String(error);

  if (
    rtkPermissionMessage === "DENIED" ||
    rtkPermissionMessage === "SYSTEM_DENIED" ||
    errorName === "NotAllowedError" ||
    errorName === "SecurityError"
  ) {
    return "permission-denied";
  }

  if (
    rtkPermissionMessage === "NO_DEVICES_AVAILABLE" ||
    errorName === "NotFoundError"
  ) {
    return "no-device";
  }

  if (
    rtkPermissionMessage === "COULD_NOT_START" ||
    errorName === "AbortError" ||
    errorName === "NotReadableError"
  ) {
    return "device-busy-or-aborted";
  }

  if (
    errorName === "NotAllowedError" ||
    PLAYBACK_BLOCKED_ERROR_PATTERN.test(errorMessage)
  ) {
    return "playback-blocked";
  }

  return "unknown";
};

export const isRecoverableAudioError = (
  error: unknown,
  rtkPermissionMessage: string | null = null
) => {
  const issueKind = classifyAudioIssueKind(error, rtkPermissionMessage);
  return issueKind === "device-busy-or-aborted" || issueKind === "no-device";
};

export const reportTableCallAudioError = (
  error: unknown,
  {
    event,
    direction,
    operation,
    issueKind,
    meeting,
    currentMeeting,
    status,
    audioReadiness,
    preferredMicrophoneId,
    preferredSpeakerId,
    participantDirectory,
    target,
    extra,
  }: ReportTableCallAudioErrorArgs
) => {
  try {
    const errorLike = error as { name?: string; message?: string } | undefined;
    const errorName = errorLike?.name ?? String(error);
    const errorMessage = errorLike?.message ?? String(error);

    recordErrorInSentry(error instanceof Error ? error : new Error(event), {
      extra: {
        ...extra,
        audioError: {
          direction,
          errorMessage,
          errorName,
          fluidId: target?.fluidId ?? null,
          issueKind: issueKind ?? classifyAudioIssueKind(error),
          operation,
          tableId: target?.tableId ?? null,
        },
        audioSnapshot: buildAudioSnapshot({
          audioReadiness,
          currentMeeting,
          meeting,
          participantDirectory,
          preferredMicrophoneId,
          preferredSpeakerId,
          status,
        }),
      },
      tags: { direction, event, feature: "table-call", operation },
    });
  } catch {
    // Error reporting must never affect call recovery behavior.
  }
};
